import { useEffect, useState, type FormEvent } from "react";
import { Mail, MapPin, Phone, Send, CheckCircle2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Magnetic from "@/components/Magnetic";
import { type CarouselApi, Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import emailjs from "@emailjs/browser";

const MAX_CHARS = 500;

/* ─── Confetti burst on form success ────────────────────────── */
const confettiColors = ["#00d2ff", "#ff00f2", "#ff3b3b", "#ffcc00", "#00ff88", "#8855ff"];

const ConfettiBurst = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-20">
    {Array.from({ length: 40 }).map((_, i) => {
      const angle = (i / 40) * 360;
      const dist = 100 + Math.random() * 220;
      const dx = Math.cos((angle * Math.PI) / 180) * dist;
      const dy = Math.sin((angle * Math.PI) / 180) * dist;
      return (
        <motion.div
          key={i}
          initial={{ x: "50%", y: "40%", scale: 0, opacity: 1 }}
          animate={{
            x: `calc(50% + ${dx}px)`,
            y: `calc(40% + ${dy}px)`,
            scale: [0, 1.2, 0.5],
            opacity: [1, 1, 0],
            rotate: Math.random() * 720 - 360,
          }}
          transition={{ duration: 1.2 + Math.random() * 0.8, ease: "easeOut" }}
          className="absolute rounded-sm"
          style={{
            width: `${4 + Math.random() * 6}px`,
            height: `${4 + Math.random() * 6}px`,
            backgroundColor: confettiColors[i % confettiColors.length],
          }}
        />
      );
    })}
  </div>
);

/* ─── Character-count SVG progress ring ─────────────────────── */
const CharRing = ({ count, max }: { count: number; max: number }) => {
  const r = 14;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(count / max, 1);
  const offset = circ - pct * circ;
  const near = pct > 0.8;
  const over = count >= max;
  return (
    <div className="flex items-center gap-2">
      <svg width="36" height="36" className="transform -rotate-90">
        <circle cx="18" cy="18" r={r} fill="none" stroke="hsl(var(--border))" strokeWidth="2.5" />
        <circle
          cx="18" cy="18" r={r} fill="none"
          stroke={over ? "hsl(var(--destructive))" : near ? "#facc15" : "hsl(var(--pulse))"}
          strokeWidth="2.5"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-300"
        />
      </svg>
      <span className={`font-body text-[10px] tracking-wider tabular-nums ${over ? "text-destructive" : near ? "text-yellow-400" : "text-foreground-muted"}`}>
        {count}/{max}
      </span>
    </div>
  );
};

/* ─── Contact section ───────────────────────────────────────── */
const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);

  // Primary EmailJS credentials First
  const PRIMARY_SERVICE_ID = "service_7wkj4oc";
  const PRIMARY_TEMPLATE_ID = "template_p6kye4e";
  const PRIMARY_PUBLIC_KEY = "cs-ddeONsSkgzQ8bV";

  // Fallback EmailJS credentials Setup
  const FALLBACK_SERVICE_ID = "service_j6zr62l";
  const FALLBACK_TEMPLATE_ID = "template_9qe0ugq";
  const FALLBACK_PUBLIC_KEY = "LQTHzsCdaVwAnxn70";

  const triggerShake = () => {
    setShaking(true);
    setTimeout(() => setShaking(false), 600);
  };

  useEffect(() => {
    if (!carouselApi) return;

    const interval = setInterval(() => {
      carouselApi.scrollNext();
    }, 3500);

    return () => clearInterval(interval);
  }, [carouselApi]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      triggerShake();
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const emailjsCom = (await import("emailjs-com")).default;
      const templateParams = {
        organization: form.name,
        name: form.name,
        from_name: form.name,
        email: form.email,
        from_email: form.email,
        reply_to: form.email,
        message: form.message,
      };

      const primary = {
        serviceId: import.meta.env.VITE_EMAILJS_PRIMARY_SERVICE_ID || PRIMARY_SERVICE_ID,
        templateId: import.meta.env.VITE_EMAILJS_PRIMARY_TEMPLATE_ID || PRIMARY_TEMPLATE_ID,
        publicKey: import.meta.env.VITE_EMAILJS_PRIMARY_PUBLIC_KEY || PRIMARY_PUBLIC_KEY,
      };

      const backup = {
        serviceId: import.meta.env.VITE_EMAILJS_BACKUP_SERVICE_ID || FALLBACK_SERVICE_ID,
        templateId: import.meta.env.VITE_EMAILJS_BACKUP_TEMPLATE_ID || FALLBACK_TEMPLATE_ID,
        publicKey: import.meta.env.VITE_EMAILJS_BACKUP_PUBLIC_KEY || FALLBACK_PUBLIC_KEY,
      };

      const attempts = [primary, backup].filter((a) => a.serviceId && a.templateId && a.publicKey);

      if (attempts.length === 0) {
        throw new Error(
          "Missing EmailJS config. Set VITE_EMAILJS_PRIMARY_SERVICE_ID, VITE_EMAILJS_PRIMARY_TEMPLATE_ID, VITE_EMAILJS_PRIMARY_PUBLIC_KEY (and optional BACKUP_* vars)."
        );
      }

      let lastError: unknown = null;
      for (const attempt of attempts) {
        try {
          await emailjsCom.send(attempt.serviceId!, attempt.templateId!, templateParams, attempt.publicKey!);
          console.log(`Email sent via ${attempt.serviceId}`);
          lastError = null;
          break;
        } catch (err) {
          console.warn(`EmailJS failed with ${attempt.serviceId}, attempting fallback...`, err);
          lastError = err;
        }
      }

      if (lastError) {
        throw lastError;
      }

      setSent(true);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      console.error("Critical EmailJS error on both services:", err);
      triggerShake();
      const message = err instanceof Error ? err.message : "Failed to send message. Please try again.";
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-8 relative overflow-hidden bg-background">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-pulse/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-electric/10 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-6xl mx-auto px-6 relative">
        {/* Calendar ecosystem carousel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16 rounded-3xl border border-white/5 bg-gradient-to-r from-pulse/5 via-background/60 to-electric/10 p-6 md:p-8 shadow-lg"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
            <div className="max-w-md space-y-3">
              <p className="font-body text-[10px] uppercase tracking-[0.3em] text-pulse">Calendar Ecosystem</p>
              <h3 className="font-display text-2xl md:text-3xl text-foreground">
                Seamless with the tools your team already uses
              </h3>
              <p className="font-body text-sm md:text-base text-foreground-muted">
                Pulse plugs into the major calendar providers so your agents can schedule, reschedule, and coordinate
                without breaking your existing workflows.
              </p>
            </div>

            <div className="w-full md:max-w-md relative">
              <Carousel
                className="w-full"
                opts={{
                  align: "start",
                  loop: true,
                }}
                setApi={setCarouselApi}
              >
                <CarouselContent>
                  {[
                    {
                      label: "Google Calendar",
                      tag: "Google",
                      href: "https://calendar.google.com",
                      description: "Two‑way sync with your team and personal calendars inside Google Workspace.",
                    },
                    {
                      label: "Outlook Calendar",
                      tag: "Outlook",
                      href: "https://outlook.live.com/calendar/",
                      description: "Coordinate meetings across Outlook and Microsoft 365 with no extra clicks.",
                    },
                    {
                      label: "Apple Calendar",
                      tag: "Apple",
                      href: "https://www.icloud.com/calendar",
                      description: "Keep iCloud events in lockstep with your AI‑driven booking flows.",
                    },
                    {
                      label: "Cal.com",
                      tag: "Cal",
                      href: "https://cal.com/pulsechatsolutions/15min",
                      description: "Use Cal.com as your central layer for routing and round‑robin scheduling.",
                    },
                    {
                      label: "Zoho Calendar",
                      tag: "Zoho",
                      href: "https://www.zoho.com/calendar/",
                      description: "Drop straight into Zoho without rewriting your scheduling playbook.",
                    },
                    {
                      label: "ICS Feeds",
                      tag: "ICS",
                      href: "https://en.wikipedia.org/wiki/ICalendar",
                      description: "Subscribe to or publish iCal feeds for custom systems and legacy tools.",
                    },
                    {
                      label: "Microsoft 365",
                      tag: "Microsoft",
                      href: "https://www.microsoft.com/microsoft-365",
                      description: "Enterprise‑grade coordination with Teams, Outlook, and the broader M365 stack.",
                    },
                  ].map((provider) => (
                    <CarouselItem key={provider.label} className="pr-4">
                      <div className="rounded-2xl bg-background/80 border border-white/10 p-5 shadow-md space-y-3">
                        <div className="inline-flex items-center gap-2 rounded-full bg-pulse/10 px-3 py-1">
                          <span className="h-6 w-6 rounded-full bg-pulse text-primary-foreground font-display text-xs flex items-center justify-center">
                            {provider.tag[0]}
                          </span>
                          <span className="font-body text-[10px] uppercase tracking-[0.25em] text-pulse">
                            {provider.tag}
                          </span>
                        </div>
                        <h4 className="font-display text-lg text-foreground">{provider.label}</h4>
                        <p className="font-body text-sm text-foreground-muted">{provider.description}</p>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious />
                <CarouselNext />
              </Carousel>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-8">
          <span className="font-body text-[10px] uppercase tracking-[0.4em] text-pulse bg-pulse/10 px-3 py-1 rounded-full">Global Presence</span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground mt-4">
            Initiate <em className="text-gradient-pulse not-italic">Connection</em>
          </h2>
          <p className="font-body text-foreground-muted mt-6 max-w-xl mx-auto leading-relaxed text-lg">
            Ready to integrate next-gen AI? Reach out to our team and we&apos;ll help you scale your digital communication.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-5 gap-16 items-start">
          {/* ── Contact info column ── */}
          <div className="md:col-span-2 space-y-10">
            <div className="space-y-8">
              {[
                { icon: Mail, label: "Email", value: "PulseChatSolutions@gmail.com", href: "mailto:PulseChatSolutions@gmail.com" },
                { icon: MapPin, label: "Platform", value: "Pulse Network", href: "#" },
                { icon: Phone, label: "Phone", value: "267-635-0692", href: "tel:2676350692" },
                { icon: Send, label: "Schedule", value: "15 min Chat", href: "https://cal.com/pulsechatsolutions/15min" },
              ].map((item, i) => (
                <motion.a
                  key={item.label}
                  href={item.href}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-center gap-6 group haptic-hover color-shift"
                >
                  <Magnetic strength={0.2}>
                    <div className="w-14 h-14 rounded-2xl border border-pulse/20 flex items-center justify-center text-pulse bg-pulse/5 group-hover:bg-pulse group-hover:text-primary-foreground transition-all duration-500 shadow-sm">
                      <item.icon size={20} />
                    </div>
                  </Magnetic>
                  <div>
                    <p className="font-body text-xs text-pulse uppercase tracking-[0.2em] mb-1">{item.label}</p>
                    <p className="font-body text-lg text-foreground font-medium group-hover:text-pulse transition-colors">{item.value}</p>
                  </div>
                </motion.a>
              ))}
            </div>
          </div>

          {/* ── Form column ── */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="md:col-span-3 glass-card p-6 md:p-8 rounded-[2rem] border border-white/5 shadow-2xl relative overflow-hidden"
          >
            <AnimatePresence mode="wait">
              {sent ? (
                <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="h-full flex flex-col items-center justify-center text-center py-10 relative">
                  <ConfettiBurst />
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1, rotate: 360 }}
                    transition={{ type: "spring", damping: 10, stiffness: 100 }}
                    className="w-20 h-20 rounded-full bg-pulse/20 flex items-center justify-center text-pulse mb-8 border border-pulse/30 shadow-pulse/20 shadow-xl"
                  >
                    <CheckCircle2 size={40} />
                  </motion.div>
                  <h3 className="font-display text-4xl text-foreground mb-4">Pulse Established!</h3>
                  <p className="font-body text-foreground-muted text-lg max-w-sm">Connection request received. Our AI will notify a human representative shortly.</p>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => { setSent(false); setSubmitError(null); setForm({ name: "", email: "", message: "" }); }}
                    className="mt-10 font-body text-sm text-pulse border-b border-pulse/30 hover:border-pulse transition-all"
                  >
                    Send another message
                  </motion.button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleSubmit}
                  className={`space-y-8 ${shaking ? "animate-shake" : ""}`}
                >
                  {submitError ? (
                    <div className="rounded-2xl border border-destructive/30 bg-destructive/10 px-5 py-4">
                      <p className="font-body text-sm text-destructive">{submitError}</p>
                    </div>
                  ) : null}
                  <div className="grid sm:grid-cols-2 gap-8">
                    <div className="relative group">
                      <label className="font-body text-[10px] uppercase tracking-widest text-pulse/80 block mb-3 ml-1 font-bold">Your Organization</label>
                      <input
                        type="text"
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className={`w-full bg-white/5 border rounded-2xl px-5 py-4 text-foreground font-body focus:outline-none focus:border-pulse/50 focus:bg-white/10 transition-all duration-300 ${shaking && !form.name.trim() ? "border-destructive/60" : "border-white/10"}`}
                        placeholder="Pulse Chat"
                      />
                    </div>
                    <div className="relative group">
                      <label className="font-body text-[10px] uppercase tracking-widest text-pulse/80 block mb-3 ml-1 font-bold">System Email</label>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className={`w-full bg-white/5 border rounded-2xl px-5 py-4 text-foreground font-body focus:outline-none focus:border-pulse/50 focus:bg-white/10 transition-all duration-300 ${shaking && !form.email.trim() ? "border-destructive/60" : "border-white/10"}`}
                        placeholder="your email"
                      />
                    </div>
                  </div>
                  <div className="relative group">
                    <div className="flex items-center justify-between mb-3 ml-1">
                      <label className="font-body text-[10px] uppercase tracking-widest text-pulse/80 font-bold">Integration Brief</label>
                      <CharRing count={form.message.length} max={MAX_CHARS} />
                    </div>
                    <textarea
                      required
                      rows={5}
                      maxLength={MAX_CHARS}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className={`w-full bg-white/5 border rounded-2xl px-5 py-4 text-foreground font-body focus:outline-none focus:border-pulse/50 focus:bg-white/10 transition-all duration-300 resize-none ${shaking && !form.message.trim() ? "border-destructive/60" : "border-white/10"}`}
                      placeholder="Describe your AI agent's purpose..."
                    />
                  </div>

                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full flex items-center justify-center gap-3 px-8 py-5 rounded-2xl bg-pulse text-primary-foreground font-body font-bold text-sm tracking-widest uppercase hover:opacity-95 transition-all shadow-xl shadow-pulse/20 disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }} className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full" />
                      ) : (
                        <>Send My Message <Send size={16} /></>
                      )}
                    </button>
                  </motion.div>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
